import { type RootState } from '@/store';

import { USER_LOAN_PROGRAM } from '@/config/constants';
import { HOMEOWNERS_INSURANCE_RATE } from '@/utils/calculator/assumptions';
import { estimateStateClosingTaxes } from '@/utils/fees/stateClosingTaxes';
import {
  CalculateClosingDisclosureProps,
  calculateTaxFactor,
  chooseMarketInterestRate,
} from '.';
// Enhanced interest rate calculation based on LTV and credit score
// Now integrates with the interest rate system
import { calculateEnhancedInterestRate } from './rateIntegration';

/** Exported for reuse by calculatePurchasingPower (needs a real PMI figure, not a flat guess). */
export const calculatePrivateMortgageInsurance = (
  loanAmount: number,
  purchasePrice: number,
  creditScore: number
) => {
  const basePremiumRates = {
    excellentCredit: {
      LTV80: 0.0,
      LTV81: 0.0008,
      LTV85: 0.001,
      LTV90: 0.0015,
      LTV95: 0.002,
      LTVOver95: 0.0025,
    },
    goodCredit: {
      LTV80: 0.0,
      LTV81: 0.0008,
      LTV85: 0.0015,
      LTV90: 0.002,
      LTV95: 0.003,
      LTVOver95: 0.004,
    },
    fairCredit: {
      LTV80: 0.0,
      LTV85: 0.003,
      LTV90: 0.006,
      LTV95: 0.007,
      LTVOver95: 0.008,
    },
    poorCredit: {
      LTV80: 0.0,
      LTV85: 0.0075,
      LTV90: 0.0085,
      LTV95: 0.0095,
      LTVOver95: 0.011,
    },
  };
  type BasePremiumRateType =
    | 'excellentCredit'
    | 'goodCredit'
    | 'fairCredit'
    | 'poorCredit';
  type LTVType = 'LTV80' | 'LTV85' | 'LTV90' | 'LTV95' | 'LTVOver95';

  let creditTier: BasePremiumRateType = 'poorCredit';
  if (creditScore >= 750) {
    creditTier = 'excellentCredit';
  } else if (creditScore >= 700) {
    creditTier = 'goodCredit';
  } else if (creditScore >= 650) {
    creditTier = 'fairCredit';
  } else {
    creditTier = 'poorCredit';
  }

  let LTV: LTVType = 'LTVOver95';
  if (loanAmount / purchasePrice <= 0.8) {
    LTV = 'LTV80';
  } else if (loanAmount / purchasePrice <= 0.85) {
    LTV = 'LTV85';
  } else if (loanAmount / purchasePrice <= 0.9) {
    LTV = 'LTV90';
  } else if (loanAmount / purchasePrice <= 0.95) {
    LTV = 'LTV95';
  } else {
    LTV = 'LTVOver95';
  }

  const basePremiumRate = basePremiumRates[creditTier][LTV];

  const PMIRate = basePremiumRate * loanAmount;
  const monthlyPMI = PMIRate / 12;
  return monthlyPMI;
};

const calculateConventionalInterestRate = (
  baseRate: number,
  creditScore: number,
  ltv: number,
  state?: RootState,
  loanTerm?: number
) => {
  // If we have access to the Redux state, use the enhanced calculation
  if (state) {
    return calculateEnhancedInterestRate(
      state,
      'Conventional',
      baseRate,
      creditScore,
      ltv,
      loanTerm
    );
  }

  // Fallback to original calculation if no state provided
  let rateAdjustment = 0;

  // Credit score adjustments
  if (creditScore >= 760) {
    rateAdjustment -= 0.25;
  } else if (creditScore >= 740) {
    rateAdjustment -= 0.125;
  } else if (creditScore >= 720) {
    rateAdjustment -= 0.0625;
  } else if (creditScore >= 700) {
    // No change
  } else if (creditScore >= 680) {
    rateAdjustment += 0.125;
  } else if (creditScore >= 660) {
    rateAdjustment += 0.25;
  } else if (creditScore >= 640) {
    rateAdjustment += 0.375;
  } else if (creditScore >= 620) {
    rateAdjustment += 0.5;
  } else if (creditScore >= 600) {
    rateAdjustment += 0.625;
  } else if (creditScore >= 580) {
    rateAdjustment += 0.75;
  } else if (creditScore >= 560) {
    rateAdjustment += 0.875;
  } else if (creditScore >= 540) {
    rateAdjustment += 1.0;
  } else if (creditScore >= 520) {
    rateAdjustment += 1.125;
  } else {
    rateAdjustment += 1.25;
  }

  // LTV adjustments
  if (ltv > 0.95) {
    rateAdjustment += 0.375;
  } else if (ltv > 0.9) {
    rateAdjustment += 0.25;
  } else if (ltv > 0.85) {
    rateAdjustment += 0.125;
  } else if (ltv > 0.8) {
    rateAdjustment += 0.0625;
  } else {
    // LTV <= 80%, no additional adjustment
  }

  return Math.max(0, baseRate + rateAdjustment);
};

export const calculateConventionalClosingDisclosure = (
  {
    offerPrice,
    listPrice,
    downPayment = 0,
    zipCode = 0,
    hoaFees = 0,
    commission = 0,
    monthlyPropertyTaxes: monthlyPropertyTaxesOverride,
    monthlyHomeownersInsurance: monthlyHomeownersInsuranceOverride,
    marketInterestRate = 6.75,
    creditScore = 0,
    loanTerm = 30,
    removePrepaymentPenalty = false,
  }: CalculateClosingDisclosureProps,
  state?: RootState
) => {
  const purchasePrice = offerPrice || listPrice;
  const loanAmount = purchasePrice - downPayment;
  const ltv = loanAmount / purchasePrice;

  const taxFactor = calculateTaxFactor(zipCode);

  // Enhanced interest rate calculation
  let interestRate = calculateConventionalInterestRate(
    marketInterestRate,
    creditScore,
    ltv,
    state,
    loanTerm
  );

  // Prepayment penalty is not available for conventional loans
  // removePrepaymentPenalty parameter is ignored

  //change on user input on account page
  const numberOfPayments = loanTerm * 12;

  // constant fees
  const titleTitleSearchBase = 250;
  const recordingDeedBase = 75;
  const appraisalFeeBase = 550;
  const underwritingFee = 1295;
  const applicationFeeBase = 495;
  const recordingTitle = 0; // No separate title recording fee
  const recordingMortgage = 75;
  const surveyFee = 250;
  const pestInspectionFee = 145;
  const floodMonitoringFee = 25;
  const floodDeterminationFee = 35;
  const creditReportFeeBase = 150;
  const processingFeeBase = 795;
  const titleSettlementAgentFee = 650;
  const earnestMoneyDeposit = 1000;
  const insuranceBinder = 150;
  const lenderCredits = 0;
  const initialEscrowMortgageInsurance = 0;
  // Tax proration depends on the closing date; no flat credit is assumed.
  const prepaidsPropertyTaxes = 0;
  const countyRecordingFees = 150;
  const stateRecordingFees = 250;

  // factored calculations
  const annualPropertyTax = (purchasePrice * taxFactor) / 100;
  //UNFACTORED CALCULATIONS, NEED TO FIGURE OUT HOW TO INCLUDE CREDIT AND STATE FACTORS
  const optionalOwnersTitleInsuranceBase = purchasePrice * 0.00575; //State, Credit
  const minimumOwnersTitle = 650;
  const maximumOwnersTitle = 3850;
  const optionalOwnersTitleInsurance = Math.min(
    Math.max(optionalOwnersTitleInsuranceBase, minimumOwnersTitle),
    maximumOwnersTitle
  );
  const recordingTransferTaxBase = purchasePrice * 0.01; //State dependent.

  // Dynamic PMI calculation based on LTV
  const monthlyPMIPayment =
    ltv > 0.8
      ? calculatePrivateMortgageInsurance(
          loanAmount,
          purchasePrice,
          creditScore
        )
      : 0;

  // Prepayment penalty is not available for conventional loans
  const prepaymentPenaltyYear1 = 0;
  const prepaymentPenaltyYear2 = 0;
  const prepaymentPenaltyYear3 = 0;
  const prepaymentPenaltyYear4 = 0;
  const prepaymentPenaltyYear5 = 0;
  const additionalLenderOriginationFee = 0;

  const monthlyHomeownersInsuranceBase =
    (purchasePrice * HOMEOWNERS_INSURANCE_RATE) / 12;
  const monthlyHomeownersInsurance =
    monthlyHomeownersInsuranceOverride ?? monthlyHomeownersInsuranceBase;
  const upfrontHomeownersInsurance = purchasePrice * HOMEOWNERS_INSURANCE_RATE;
  const monthlyPropertyTaxesBase = annualPropertyTax / 12;
  const monthlyPropertyTaxes =
    monthlyPropertyTaxesOverride ?? monthlyPropertyTaxesBase;
  const prepaidsHomeownersInsurance =
    ((purchasePrice * HOMEOWNERS_INSURANCE_RATE) / 12) * 3;
  // Lender's policy covers the loan amount.
  const titleLendersTitleInsuranceBase = loanAmount * 0.004;
  const homeInspectionFeeBase = purchasePrice * 0.0035;
  const minimumInspectionFee = 650;
  const maximumInspectionFee = 4500;
  const homeInspectionFee = Math.min(
    Math.max(homeInspectionFeeBase, minimumInspectionFee),
    maximumInspectionFee
  );
  const initialEscrowTaxes = monthlyPropertyTaxes * 6;
  const initialEscrowHOI = ((purchasePrice * 0.01) / 12) * 3;

  //these aren't going to change, they are determined
  const dailyInterestRate = interestRate / 365 / 100; // Convert annual rate to daily decimal
  const prepaidInterest = loanAmount * dailyInterestRate * 15; // Return the result rounded to 2 decimal places
  const monthlyInterestRate = interestRate / 12 / 100; // Convert annual rate to monthly decimal
  const principalAndInterest =
    (loanAmount *
      (monthlyInterestRate *
        Math.pow(1 + monthlyInterestRate, numberOfPayments))) /
    (Math.pow(1 + monthlyInterestRate, numberOfPayments) - 1);
  // Buyer's share of the state deed/transfer tax (by state custom) and the
  // borrower's loan taxes — see utils/fees/stateClosingTaxes.
  const stateTaxes = estimateStateClosingTaxes({
    zipCode,
    price: purchasePrice,
    loanAmount,
  });
  const transferTaxes = stateTaxes.transferTaxes;
  const mortgageTaxes = stateTaxes.mortgageTaxes;
  const estimatedEscrow =
    monthlyHomeownersInsurance + monthlyPropertyTaxes + monthlyPMIPayment;
  const piti = principalAndInterest + estimatedEscrow;
  const cleanPayment = piti;
  const titleCosts =
    optionalOwnersTitleInsurance +
    titleTitleSearchBase +
    titleLendersTitleInsuranceBase +
    surveyFee +
    titleSettlementAgentFee +
    insuranceBinder;
  const govCosts =
    stateRecordingFees +
    countyRecordingFees +
    recordingDeedBase +
    recordingMortgage +
    recordingTitle +
    transferTaxes +
    mortgageTaxes;
  const prepaidCosts =
    initialEscrowTaxes +
    prepaidsPropertyTaxes +
    prepaidInterest +
    prepaidsHomeownersInsurance;
  const lenderCostTotals =
    floodDeterminationFee +
    floodMonitoringFee +
    creditReportFeeBase +
    appraisalFeeBase +
    applicationFeeBase +
    underwritingFee +
    processingFeeBase;
  const closingCosts = titleCosts + lenderCostTotals + govCosts + prepaidCosts;
  const cashToClose = closingCosts + downPayment - earnestMoneyDeposit;
  const cashOutsideClosing =
    appraisalFeeBase + earnestMoneyDeposit + homeInspectionFee;
  const unbeatableCredit = (purchasePrice * commission) / 100; //this is where we put the buyers agent commission
  const unbeatableDeal = cashToClose - unbeatableCredit;

  // Perform calculations using the provided purchase price and constants
  // Prepare the closing disclosure object
  const closingDisclosure = {
    type: USER_LOAN_PROGRAM.CONVENTIONAL,
    offerPrice,
    listPrice,
    purchasePrice,
    commission,
    marketInterestRate,
    creditScore,
    downPayment,
    loanAmount,
    interestRate,
    ltv,
    upfrontHomeownersInsurance,
    cleanPayment,
    cashOutsideClosing,
    monthlyPMIPayment,
    processingFeeBase,
    principalAndInterest,
    homeInspectionFee,
    loanTerm,
    optionalOwnersTitleInsurance,
    hoaFees,
    earnestMoneyDeposit,
    piti,
    estimatedEscrow,
    monthlyPropertyTaxes,
    monthlyHomeownersInsurance,
    prepaidsHomeownersInsurance,
    prepaidsPropertyTaxes,
    prepaidInterest,
    titleSettlementAgentFee,
    titleLendersTitleInsuranceBase,
    titleTitleSearchBase,
    insuranceBinder,
    recordingDeedBase,
    appraisalFeeBase,
    underwritingFee,
    applicationFeeBase,
    pestInspectionFee,
    floodMonitoringFee,
    floodDeterminationFee,
    creditReportFeeBase,
    recordingTitle,
    recordingMortgage,
    countyRecordingFees,
    stateRecordingFees,
    surveyFee,
    initialEscrowTaxes,
    initialEscrowMortgageInsurance,
    initialEscrowHOI,
    lenderCredits,
    lenderCostTotals,
    titleCosts,
    govCosts,
    prepaidCosts,
    closingCosts,
    // Exactly what closingCosts sums — the fee breakdown itemizes these.
    closingCostParts: {
      optionalOwnersTitleInsurance,
      titleTitleSearchBase,
      titleLendersTitleInsuranceBase,
      surveyFee,
      titleSettlementAgentFee,
      insuranceBinder,
      floodDeterminationFee,
      floodMonitoringFee,
      creditReportFeeBase,
      appraisalFeeBase,
      applicationFeeBase,
      underwritingFee,
      processingFeeBase,
      stateRecordingFees,
      countyRecordingFees,
      recordingDeedBase,
      recordingMortgage,
      recordingTitle,
      transferTaxes,
      mortgageTaxes,
      initialEscrowTaxes,
      prepaidsPropertyTaxes,
      prepaidInterest,
      prepaidsHomeownersInsurance,
    },
    cashToClose,
    unbeatableDeal,
    transferTaxes,
    mortgageTaxes,
    stateTaxLines: stateTaxes.lines,
    stateTaxState: stateTaxes.state,
    stateTaxNote: stateTaxes.localNote,

    // Prepayment penalty is not available for conventional loans
    removePrepaymentPenalty: false,
    prepaymentPenaltyYear1: 0,
    prepaymentPenaltyYear2: 0,
    prepaymentPenaltyYear3: 0,
    prepaymentPenaltyYear4: 0,
    prepaymentPenaltyYear5: 0,
    additionalLenderOriginationFee: 0,
  };
  return closingDisclosure;
};

export type ProductConventionalEsitmation = ReturnType<
  typeof calculateConventionalClosingDisclosure
>;
